import { useEffect, useState } from 'react';
import type { SkillManifest, SkillUsage } from '@reef/schema';
import { api } from '../api.ts';

/**
 * The skills registry.
 *
 * A directory listing tells you a skill exists. What decides whether a registry
 * of hundreds stays usable is the other three columns: what its lint says, how
 * often anything ran it, and how often those runs failed. A skill nothing has
 * run in a month is either dead or the most important thing here and nobody
 * knows it — and the list is where somebody notices.
 *
 * There is no edit button, and that is the design rather than an omission. A
 * skill is a committed document; changing one is a commit to
 * `skills/<name>/SKILL.md`, which is reviewable in a diff, revertible, and
 * attributable. A textarea here would be a second way to change what every
 * agent does, and the only one with no history.
 */
export function SkillsView() {
  const [skills, setSkills] = useState<SkillManifest[]>([]);
  const [usage, setUsage] = useState<Record<string, SkillUsage>>({});
  const [selected, setSelected] = useState<string | null>(null);
  const [detail, setDetail] = useState<{
    instructions: string;
    proposals: Array<{ id: string; claim: string; state: string; createdAt: string }>;
  } | null>(null);
  const [windowHours, setWindowHours] = useState(24 * 7);
  /*
   * Eval standing per skill, alongside the lint and the usage.
   *
   * The three answer different halves of one question. The lint says the
   * document is well-formed, usage says something runs it, and this says it
   * still reaches the judgements it is supposed to — which is the only one of
   * the three that survives a rewrite of the instructions.
   */
  const [evals, setEvals] = useState<
    Record<string, { cases: number; passed: number; failed: number; skipped: number; lastRanAt: string | null }>
  >({});
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let current = true;
    api
      .skillRegistry(windowHours)
      .then((result) => {
        if (!current) return;
        setSkills(result.skills);
        setUsage(Object.fromEntries(result.usage.map((entry) => [entry.skill, entry])));
      })
      .catch((caught) => current && setError((caught as Error).message));
    return () => {
      current = false;
    };
  }, [windowHours]);

  useEffect(() => {
    api
      .skillEvals()
      .then((result) =>
        setEvals(Object.fromEntries(result.summary.map((entry) => [entry.skill, entry]))),
      )
      .catch(() => {
        // A deployment that has never run the suite has no results, which is a
        // normal state and not worth an error banner over the whole page.
      });
  }, []);

  useEffect(() => {
    if (!selected) {
      setDetail(null);
      return;
    }
    let current = true;
    api
      .skill(selected)
      .then((result) => current && setDetail({ instructions: result.instructions, proposals: result.proposals }))
      .catch((caught) => current && setError((caught as Error).message));
    return () => {
      current = false;
    };
  }, [selected]);

  const failing = skills.filter((skill) =>
    (skill.findings ?? []).some((finding) => finding.severity === 'error'),
  ).length;

  return (
    <div className="studio">
      <header className="studio-head">
        <span className="studio-crumb">
          <span className="studio-crumb-root">Skills</span>
          {selected && <span className="studio-crumb-sep">/</span>}
          {selected && <span>{selected}</span>}
        </span>
        <span className="spacer" />
        <span className={`chip ${failing > 0 ? 'warn' : ''}`}>
          {skills.length} skills · {failing} failing lint
        </span>
        <label className="studio-window">
          Window
          <select
            value={windowHours}
            onChange={(event) => setWindowHours(Number(event.target.value))}
            aria-label="Usage window"
          >
            <option value={24}>24 hours</option>
            <option value={24 * 7}>7 days</option>
            <option value={24 * 30}>30 days</option>
          </select>
        </label>
      </header>

      <div className="studio-column wide">
        {error && <div className="banner danger">{error}</div>}

        <section className="studio-card">
          <div className="studio-card-head">
            <h2>Registry</h2>
          </div>
          <div className="studio-card-body">
            <table className="studio-table">
              <thead>
                <tr>
                  <th>Skill</th>
                  <th>Steps</th>
                  <th className="numeric">Runs</th>
                  <th className="numeric">Failures</th>
                  <th>Evals</th>
                  <th>Lint</th>
                </tr>
              </thead>
              <tbody>
                {skills.map((skill) => {
                  const used = usage[skill.name];
                  const errors = (skill.findings ?? []).filter(
                    (finding) => finding.severity === 'error',
                  ).length;
                  const warnings = (skill.findings ?? []).length - errors;

                  return (
                    <tr
                      key={skill.name}
                      aria-current={selected === skill.name}
                      onClick={() => setSelected(skill.name)}
                    >
                      <td>
                        <span className="studio-agent-name">{skill.name}</span>
                        {skill.superseded && <span className="chip">superseded</span>}
                        <div className="tile-note">{skill.description || 'no description'}</div>
                      </td>
                      <td>
                        {skill.stepCount === 0 ? (
                          /*
                            The most useful cell on the page. A skill nothing
                            mounts is one nobody will notice rotting, and the
                            list is the only place that fact ever surfaces.
                          */
                          <span className="tile-note">nothing runs it</span>
                        ) : (
                          `${skill.stepCount} in ${skill.usedBy?.length ?? 0} workflows`
                        )}
                      </td>
                      <td className="numeric">{used?.runs ?? 0}</td>
                      <td className="numeric">
                        {used && used.failureRate !== null
                          ? `${Math.round(used.failureRate * 100)}%`
                          : '—'}
                      </td>
                      <td>
                        {(() => {
                          const standing = evals[skill.name];
                          if (!standing || standing.cases === 0) {
                            // Said plainly rather than left blank: a skill with
                            // no case is one whose behaviour nothing protects,
                            // and a blank cell reads as "fine".
                            return <span className="tile-note">no cases</span>;
                          }
                          if (standing.failed > 0) {
                            return <span className="chip warn">{standing.failed} failing</span>;
                          }
                          if (standing.passed === 0) {
                            // Skipped is not passed. A suite that reported green
                            // on a machine that executed nothing is the failure
                            // this distinction exists to prevent.
                            return <span className="tile-note">{standing.cases} not run</span>;
                          }
                          return <span className="tile-note">{standing.passed} passing</span>;
                        })()}
                      </td>
                      <td>
                        {errors > 0 ? (
                          <span className="chip warn">{errors} errors</span>
                        ) : warnings > 0 ? (
                          <span className="chip">{warnings} warnings</span>
                        ) : (
                          <span className="tile-note">clean</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <p className="hint">
              A failure rate of “—” means nothing that mounted this skill has ended yet. It is not
              zero: a new skill and a skill that has never failed are different facts.
            </p>
          </div>
        </section>

        {selected && (
          <section className="studio-card">
            <div className="studio-card-head">
              <h2>{selected}</h2>
              <span className="chip">committed at skills/{selected}/SKILL.md</span>
            </div>
            <div className="studio-card-body">
              {(skills.find((skill) => skill.name === selected)?.findings ?? []).map((finding) => (
                <div
                  key={`${finding.rule}-${finding.line ?? 0}`}
                  className={`banner ${finding.severity === 'error' ? 'danger' : 'warn'}`}
                >
                  <strong>{finding.rule}</strong> — {finding.message}
                </div>
              ))}

              {detail?.proposals.length ? (
                <>
                  <h3>Proposals waiting</h3>
                  <table>
                    <tbody>
                      {detail.proposals.map((proposal) => (
                        <tr key={proposal.id}>
                          <td>{proposal.claim}</td>
                          <td>{proposal.state}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <p className="hint">
                    Accepting one is a commit that bumps this skill’s version, which is why they
                    wait here rather than being applied.
                  </p>
                </>
              ) : null}

              <pre className="studio-instructions">{detail?.instructions ?? 'Loading…'}</pre>
            </div>
          </section>
        )}
      </div>
    </div>
  );
}
